import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Grid3X3, FileText, Settings, LogOut, Sun, Moon, CreditCard, Rocket, Building2, ShieldAlert, Inbox } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { exitPartnerContext, isInPartnerContext } from '../lib/partnerContext';
import { useTheme } from '../contexts/ThemeContext';
import { useMyTenant } from '../hooks/useMyTenant';
import { useInternalAdminVisibility } from '../hooks/useInternalAdminVisibility';
import { getAccountSectionLabel } from '../lib/tier-features';
import BluePrintLogo from './BluePrintLogo';
import NotificationBell from './NotificationBell';
import TenantSwitcher from './TenantSwitcher';
import UsageMeter from './UsageMeter';

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();
}

// Display labels for the small role line under the user's email in the
// sidebar footer. The role here is the *tenant* role from
// /tenants/me/membership (tenant_members.role). In practice that
// column takes one of TENANT_ADMIN | TENANT_MEMBER | CLIENT, plus the
// legacy ADMIN alias some pre-migration rows still use (matches the
// hierarchy in tenant-auth.middleware and useMyTenant.isAdminRole).
//
// CLIENT is intentionally NOT labelled. CLIENT users do mount this
// sidebar (e.g. on /projects), but they already know they're an
// external stakeholder from context — surfacing "Client" adds no
// actionable information and would just be visual noise. Easy to
// add later if clients ask for it.
//
// Project-level roles (DEVELOPER, PRODUCT_MANAGER, etc.) live in
// project_members and never flow through this hook. SUPER_ADMIN lives
// in platform_admins and is also not exposed by /me/membership.
// Anything not in this map falls through to render nothing rather
// than leaking a raw enum string or "undefined".
const ROLE_LABELS: Record<string, string> = {
  TENANT_ADMIN:  'Admin',
  ADMIN:         'Admin',
  TENANT_MEMBER: 'Member',
};

function roleLabel(role: string | undefined | null): string | null {
  if (!role) return null;
  return ROLE_LABELS[role] ?? null;
}

// Settings nav-item label is tier-aware (issue #779): solo plans see
// "Account" since "Settings" + "Manage Organisation" wording implies a
// multi-person org that doesn't exist for them. Resolved at render time
// because the Settings link itself is shared across tiers.
const baseNavItemsWith = (settingsLabel: string) => [
  { to: '/dashboard',  label: 'Dashboard', icon: Grid3X3 },
  { to: '/projects',   label: 'Projects',  icon: LayoutDashboard },
  { to: '/artefacts',  label: 'Artefacts', icon: FileText },
  { to: '/settings',   label: settingsLabel, icon: Settings },
];

export default function Sidebar() {
  const { user, logout, setSession } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { myTenant } = useMyTenant();
  const internalAdminVisible = useInternalAdminVisibility();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const settingsLabel = getAccountSectionLabel(myTenant?.subscriptionTier, 'sidebar');
  const baseNavItems = baseNavItemsWith(settingsLabel);

  // Billing + Partner Portal nav entries are gated on partner_accounts
  // membership — the portal itself redirects non-partners to /projects, so
  // surfacing either link for them would be a dead end. Partner identity is
  // orthogonal to tier. Inserted above Settings so related admin items
  // cluster. #1848 — Partner Portal is otherwise only reachable via the
  // onboarding wizard's one-time post-completion redirect; without a
  // standing nav entry a returning partner admin has no way back to /partner.
  // isPartner (partner_account_members membership) can be true before the
  // partner's own onboarding wizard (partner_onboarding_wizard.completedAt,
  // a separate signal not surfaced here) finishes — e.g. immediately after
  // POST /admin/partners provisions the account. Deliberately NOT gated on
  // that too: PartnerPortalPage already redirects an incomplete-wizard
  // partner to /partner/onboarding on its own (PartnerPortalPage.tsx), the
  // same "expose the destination, let it enforce its own precondition"
  // pattern the Setup nav item below uses for setupComplete.
  const withBilling = myTenant?.isPartner
    ? [
        ...baseNavItems.slice(0, 3),
        { to: '/partner', label: 'Partner Portal', icon: Building2 },
        { to: '/settings/billing', label: 'Billing', icon: CreditCard },
        ...baseNavItems.slice(3),
      ]
    : baseNavItems;

  // #2762 — Workbasket nav entry, internal/Delivery-tier only. GET
  // /dashboard/workbasket 403s a CLIENT-only caller outright (the
  // Business-tier equivalent is the client portal's own assigned-items view,
  // #2761), so surfacing this link for CLIENT would be a dead end. Inserted
  // by finding '/dashboard' rather than a fixed index — robust regardless of
  // whether the Partner Portal/Billing splice above already ran.
  const withWorkbasket = myTenant?.role !== 'CLIENT'
    ? (() => {
        const dashboardIdx = withBilling.findIndex(item => item.to === '/dashboard');
        const withNav = [...withBilling];
        withNav.splice(dashboardIdx + 1, 0, { to: '/workbasket', label: 'Workbasket', icon: Inbox });
        return withNav;
      })()
    : withBilling;

  // D4b (#1247) — persistent "Setup" entry, shown WHILE the setup-as-a-project
  // RING flow is incomplete and hidden once it's done. Visibility is the real
  // completion signal (setupComplete) threaded from /tenants/me/membership via
  // useMyTenant — same signal the DefaultsCard uses, no extra fetch. Strict
  // `=== false` so the item stays hidden while the signal is still loading
  // (undefined) or absent on legacy responses. Placed first so it reads as the
  // primary "finish getting started" affordance; links to the /setup hub.
  //
  // #1247 access-regression follow-on — ALSO require setupAccessible === true.
  // setupComplete is tenant-wide; the /setup gate is per-identity. Showing the
  // item on completion alone routed the Buyer (once a TL is assigned) and any
  // non-Buyer/non-TL member into a page the gate 403s. setupAccessible carries
  // the gate's exact predicate per-caller; strict `=== true` keeps the item
  // hidden while the signal is loading/absent (fail-closed).
  const navItems = myTenant?.setupComplete === false && myTenant?.setupAccessible === true
    ? [{ to: '/setup', label: 'Setup', icon: Rocket }, ...withWorkbasket]
    : withWorkbasket;

  // NavLink's default isActive prefix-matches, which means /settings/billing
  // would activate BOTH the Settings and Billing entries at once. Compute
  // active state ourselves and carve /settings/billing out of Settings' range
  // (other /settings/* subpages — organisation, byoi, agent-models — still
  // highlight Settings as before).
  const isNavActive = (to: string): boolean => {
    if (to === '/settings') {
      if (pathname === '/settings/billing' || pathname.startsWith('/settings/billing/')) {
        return false;
      }
      return pathname === '/settings' || pathname.startsWith('/settings/');
    }
    if (to === '/settings/billing') {
      return pathname === '/settings/billing' || pathname.startsWith('/settings/billing/');
    }
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className="w-60 flex-shrink-0 flex flex-col h-screen sticky top-0"
      style={{ backgroundColor: 'var(--sidebar-bg)' }}
    >
      {/* Partner context breadcrumb. Gate on isInPartnerContext() (not the raw
          key) so a STALE partnerContextToken after a re-auth doesn't show the
          breadcrumb — clicking it would otherwise restore the pre-re-auth token
          over the fresh session (#1330 review L1 / #1318). */}
      {isInPartnerContext() && (
        <button
          onClick={() => {
            if (!user) return;
            // #1330: restore the partner's own token through AuthContext
            // (setSession) so useAuth().token reverts to the partner's own scope
            // without a full page reload, and clear the context keys. If there's
            // no original token to restore, force a clean logout rather than
            // leave the client-scoped token active (review M1).
            const { returnPath, restored } = exitPartnerContext(user, setSession);
            if (!restored) logout();
            navigate(returnPath);
          }}
          className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-white/[0.08]"
          style={{ color: 'var(--primary-light)', fontFamily: 'var(--font-body)' }}
        >
          &larr; Partner Portal
        </button>
      )}

      {/* Logo — mark 32px, wordmark ~18px via size prop */}
      <div className="px-4 py-5 mb-2">
        <BluePrintLogo size={32} />
      </div>

      {/* Nav — 6px gap between items */}
      <nav className="flex-1 px-2 space-y-1.5">
        {navItems.map(({ to, label, icon: Icon }) => {
          const active = isNavActive(to);
          return (
            <NavLink
              key={to}
              to={to}
              // Ignore NavLink's built-in isActive (prefix-matches and would
              // double-highlight Settings + Billing). Use our computed value.
              className={
                active
                  ? 'relative flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors'
                  : 'flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors hover:bg-white/[0.08] hover:!text-white/85'
              }
              style={
                active
                  ? {
                      backgroundColor: 'rgba(124,58,237,0.15)',
                      color: 'var(--primary-light)',
                      fontFamily: 'var(--font-body)',
                      fontWeight: 500,
                      fontSize: 15,
                    }
                  : {
                      color: 'rgba(255,255,255,0.7)',
                      fontFamily: 'var(--font-body)',
                      fontWeight: 500,
                      fontSize: 15,
                    }
              }
              aria-current={active ? 'page' : undefined}
            >
              {/* Active indicator dot */}
              {active && (
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full"
                  style={{ backgroundColor: 'var(--primary-light)' }}
                />
              )}
              <Icon
                size={20}
                strokeWidth={active ? 2.5 : 2}
                style={{ flexShrink: 0 }}
              />
              <span>{label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Token usage meter (Individual tier only) */}
      <div className="px-2 pb-1">
        <UsageMeter />
      </div>

      {/* Notification bell + theme toggle */}
      <div className="px-2 pb-1" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <NotificationBell />
        <button
          onClick={toggleTheme}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 'var(--radius-sm)',
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.5)',
            cursor: 'pointer',
            flex: 1,
            fontSize: 14,
            fontFamily: 'var(--font-body)',
            transition: 'background 150ms, color 150ms',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
          }}
        >
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
        </button>
      </div>

      {/* Sprint INTADMIN INTADMIN.B1 (#2110) — PRD FR-05 entry point.
          Platform admins get a standing way into the internal admin area;
          everyone else must see NOTHING here.

          `internalAdminVisible` is a DISPLAY signal only — never an
          authorization decision (see useInternalAdminVisibility's header).
          The gate that matters is InternalAdminGuard plus the server-side
          chain on /api/internal/*. Forging the JWT claim this reads buys
          an attacker a link to a URL they could already type.

          Conditionally RENDERED, not hidden with CSS — FR-04 requires the
          entry point to be genuinely absent from the component tree for
          non-admins, and a `display: none` link is still in the DOM,
          discoverable by anyone who opens devtools or reads the a11y tree.

          Sits outside the tenant nav list on purpose: this is a
          platform-level destination, not another tenant page, and mixing
          it into `navItems` would put it in the same visual group as
          Projects/Artefacts. */}
      {internalAdminVisible && (
        <div className="px-2 pb-1">
          <NavLink
            // Deep-links to the panel rather than /internal, which is only a
            // guarded redirect to here. Going via /internal would mount
            // InternalAdminGuard twice in sequence — two GET
            // /api/internal/session calls, each a platform_admins lookup and
            // each logging the DEPRECATED-standard-JWT warning — for one
            // click. /internal stays routable for anyone who types it.
            to="/internal/pricing"
            data-testid="sidebar-internal-admin-link"
            className="flex items-center gap-3 px-3 py-2 rounded-lg w-full transition-colors hover:bg-white/[0.08]"
            style={{
              color:      '#fbbf24',
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
              fontSize:   14,
            }}
          >
            <ShieldAlert size={18} style={{ flexShrink: 0 }} />
            <span>Internal Admin</span>
          </NavLink>
        </div>
      )}

      {/* User footer */}
      <div className="p-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            {user ? getInitials(user.name) : '?'}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="truncate"
              style={{ color: 'var(--sidebar-text)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500 }}
            >
              {user?.name ?? ''}
            </p>
            <p
              className="truncate"
              style={{ color: 'rgba(250,250,249,0.45)', fontFamily: 'var(--font-body)', fontSize: 12 }}
            >
              {user?.email ?? ''}
            </p>
            <TenantSwitcher />
            {(() => {
              const label = roleLabel(myTenant?.role);
              if (!label) return null;
              return (
                <p
                  className="truncate"
                  data-testid="sidebar-user-role"
                  style={{ color: 'rgba(250,250,249,0.4)', fontFamily: 'var(--font-body)', fontSize: 11, marginTop: 1 }}
                >
                  {label}
                </p>
              );
            })()}
          </div>
          <button
            onClick={handleLogout}
            title="Sign Out"
            className="p-1.5 rounded-md transition-colors hover:bg-white/10"
            style={{ color: 'rgba(250,250,249,0.45)' }}
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}
